import apiClient from './apiClient';

// Types
export interface PaymentMethod {
  id: string;
  type: 'card' | 'upi' | 'netbanking' | 'wallet';
  isDefault: boolean;
  details: {
    last4?: string;
    brand?: string;
    expiryMonth?: string;
    expiryYear?: string;
    bankName?: string;
    upiId?: string;
    walletName?: string;
  };
}

export interface Invoice {
  id: string;
  number: string;
  date: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed' | 'expired';
  items: {
    description: string;
    amount: number;
    quantity: number;
  }[];
  billing: {
    name: string;
    email: string;
    address?: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      country: string;
      postalCode: string;
    };
  };
  downloadUrl?: string;
  paymentMethod: string;
  subscription?: string;
  metadata?: Record<string, any>;
  expiresAt?: string;
  createdAt?: string;
}

export interface PaymentHistoryParams {
  from?: string; // ISO date string
  to?: string; // ISO date string
  limit?: number;
  page?: number;
  status?: 'paid' | 'pending' | 'failed' | 'expired';
}

export interface PaymentHistoryResponse {
  total: number;
  page: number;
  limit: number;
  data: Invoice[];
}

export interface PaymentLinkResponse {
  success: boolean;
  message?: string;
  paymentLink?: string;
  paymentLinkId?: string;
  orderId?: string;
  amount?: number;
  transactionId?: string;
  expiresAt?: string;
}

export interface PaymentStatusResponse {
  success: boolean;
  message?: string;
  status?: 'pending' | 'completed' | 'failed';
  transactionId?: string;
  planId?: string;
}

class PaymentService {
  /**
   * Get the user's payment history
   */
  async getPaymentHistory(params?: PaymentHistoryParams): Promise<PaymentHistoryResponse> {
    try {
      console.log('PaymentService: Fetching payment history with params:', params);
      const queryParams = new URLSearchParams();
      
      if (params?.from) queryParams.append('from', params.from);
      if (params?.to) queryParams.append('to', params.to);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.status) queryParams.append('status', params.status);
      
      const queryString = queryParams.toString();
      const url = `/payment/history${queryString ? '?' + queryString : ''}`;
      
      console.log(`PaymentService: Making GET request to ${url}`);
      const response = await apiClient.get<PaymentHistoryResponse>(url);
      console.log('PaymentService: Payment history response:', response);
      return response;
    } catch (error: any) {
      console.error('PaymentService: Error fetching payment history:', error);
      
      // Log detailed error information
      if (error.response) {
        // Server responded with a status code outside the 2xx range
        console.error('PaymentService: Server error details:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
      } else if (error.request) {
        // The request was made but no response was received
        console.error('PaymentService: No response received:', error.request);
      }
      
      return {
        total: 0,
        page: 1,
        limit: 10,
        data: []
      };
    }
  }

  /**
   * Get a specific invoice by ID
   */
  async getInvoice(invoiceId: string): Promise<Invoice | null> {
    try {
      console.log(`PaymentService: Fetching invoice with ID: ${invoiceId}`);
      const url = `/payment/${invoiceId}`;
      console.log(`PaymentService: Making GET request to ${url}`);
      const response = await apiClient.get<Invoice>(url);
      console.log('PaymentService: Invoice response:', response);
      return response;
    } catch (error: any) {
      console.error(`PaymentService: Error fetching invoice ${invoiceId}:`, error);
      
      if (error.response) {
        console.error('PaymentService: Server error details:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      
      return null;
    }
  }

  /**
   * Download an invoice (returns the PDF blob directly)
   */
  async downloadInvoice(invoiceId: string, invoiceNumber: string): Promise<boolean> {
    try {
      console.log(`PaymentService: Generating invoice for download: ${invoiceId}`);
      // Use the API_BASE_URL directly or from environment variable
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const url = `${baseUrl}/payment/invoice/${invoiceId}`;
      console.log(`PaymentService: Making PDF request to ${url}`);
      
      // Show download in progress
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
          'Authorization': `Bearer ${localStorage.getItem('mocktrail-token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }
      
      // Get the blob directly from the response
      const blob = await response.blob();
      console.log('PaymentService: Invoice PDF blob received, size:', 
        blob.size ? `${Math.round(blob.size / 1024)}KB` : 'unknown');
      
      // Create a URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a link and trigger download
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `mocktrail-${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
        document.body.removeChild(a);
      }, 100);
      
      return true;
    } catch (error: any) {
      console.error(`PaymentService: Error downloading invoice ${invoiceId}:`, error);
      
      if (error.response) {
        console.error('PaymentService: Server error details:', {
          status: error.response.status,
          data: typeof error.response.data === 'string' ? 
            error.response.data.substring(0, 100) + '...' : 
            error.response.data
        });
      }
      
      return false;
    }
  }

  /**
   * Process a payment for a subscription plan with optional coupon
   */
  async processPayment(data: { 
    planId: string;
    couponCode?: string;
  }): Promise<PaymentLinkResponse> {
    try {
      console.log('PaymentService: Generating payment link with data:', data);
      const url = '/payment/create-payment-link';
      console.log(`PaymentService: Making POST request to ${url} with data:`, data);
      const response = await apiClient.post<PaymentLinkResponse>(url, data);
      console.log('PaymentService: Payment link response:', response);
      
      // Handle case where response might not have success field but has paymentLink
      if (response && response.paymentLink && !response.success) {
        return {
          ...response,
          success: true
        };
      }
      
      return response;
    } catch (error: any) {
      console.error('PaymentService: Error generating payment link:', error);
      
      let errorMessage = 'Failed to generate payment link';
      
      if (error.response) {
        console.error('PaymentService: Server error details:', {
          status: error.response.status,
          data: error.response.data
        });
        
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        message: errorMessage
      };
    }
  }

  /**
   * Check the status of a payment by orderId or using paymentLinkId
   * orderId can be order_... or sub_...
   * This is used after a Razorpay payment is completed to check if the payment was successful
   * Razorpay will directly call the backend callback endpoint, so we just need to check the status
   */
  async checkPaymentStatus(orderId: string, paymentLinkId: string): Promise<PaymentStatusResponse> {
    try {
      console.log('PaymentService: Checking payment status for orderId:', orderId);
      var url = `/payment/status`;
      // Apply query params
      if(paymentLinkId && paymentLinkId !== '') {
        url += `?paymentLinkId=${paymentLinkId}`;
      } else if(orderId && orderId !== '') {
        url += `?orderId=${orderId}`;
      }
      console.log(`PaymentService: Making GET request to ${url}`);
      const response = await apiClient.get<PaymentStatusResponse>(url);
      console.log('PaymentService: Payment status response:', response);
      
      return {
        status: response.status || 'pending',
        ...response,
        success: true // Ensure success is true and overrides any value from response
      };
    } catch (error: any) {
      console.error('PaymentService: Error checking payment status:', error);
      
      let errorMessage = 'Failed to check payment status';
      
      if (error.response) {
        console.error('PaymentService: Server error details:', {
          status: error.response.status,
          data: error.response.data
        });
        
        if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        status: 'failed',
        message: errorMessage
      };
    }
  }

  /**
   * Helper function to check if a payment is expired
   */
  private checkIfPaymentExpired(payment: any): boolean {
    // If it's already marked as expired, return true
    if (payment.status === 'expired') return true;
    
    // If it's pending and has an expiration date, check if it's expired
    if (payment.status === 'pending' && payment.expiresAt) {
      try {
        const expiryDate = new Date(payment.expiresAt).getTime();
        const now = new Date().getTime();
        
        // Log expiry info for debugging
        console.log(`Payment ${payment._id || payment.id}: Expires at ${new Date(expiryDate).toISOString()}, now: ${new Date(now).toISOString()}, expired: ${expiryDate < now}`);
        
        return expiryDate < now;
      } catch (error) {
        console.error('Error checking payment expiration:', error);
        return false;
      }
    }
    
    return false;
  }
  
  /**
   * Cancel a payment transaction
   */
  async cancelPayment(transactionId: string): Promise<PaymentStatusResponse> {
    try {
      console.log(`PaymentService: Cancelling payment transaction: ${transactionId}`);
      const url = `/payment/cancel/${transactionId}`;
      console.log(`PaymentService: Making POST request to ${url}`);
      
      const response = await apiClient.post<PaymentStatusResponse>(url, {});
      console.log("PaymentService: Cancel payment response:", response);
      
      return response;
    } catch (error: any) {
      console.error(`PaymentService: Error cancelling payment ${transactionId}:`, error);
      
      if (error.response) {
        console.error("PaymentService: Server error details:", {
          status: error.response.status,
          data: error.response.data
        });
        
        // Return the error response if available
        return {
          success: false,
          message: error.response.data?.message || `Failed to cancel payment: ${error.response.status}`,
          status: "failed"
        };
      }
      
      return {
        success: false,
        message: error.message || "Failed to cancel payment",
        status: "failed"
      };
    }
  }
  
  processInvoices(rawData: any): Invoice[] {
    if (!rawData || typeof rawData !== 'object') {
      console.error('PurchaseHistory: Invalid payment history data:', rawData);
      return [];
    }
    
    let processedInvoices: Invoice[] = [];
    
    // Check if the response is in normalized format
    if ('_response' in rawData) {
      const keys = Object.keys(rawData).filter(key => key !== '_response');
      
      if (keys.some(key => !isNaN(Number(key)))) {
        // Extract transactions from numeric keys
        const transactions = keys
          .filter(key => !isNaN(Number(key)))
          .map(key => {
            const tx = (rawData as Record<string, any>)[key];
            
            // Ensure each transaction has required properties
            if (tx && typeof tx === 'object') {
              // Check if the payment is expired
              const isExpired = this.checkIfPaymentExpired(tx);
              const status = isExpired ? 'expired' : (tx.status || 'paid');
              
              return {
                id: tx._id || tx.id || `invoice-${key}`,
                number: tx.invoiceNumber || tx._id?.substring(0, 8) || `INV-${key}`,
                date: tx.createdAt || tx.updatedAt,
                amount: typeof tx.amount === 'number' ? tx.amount : 0,
                currency: tx.currency || 'INR',
                status: status,
                paymentMethod: tx.paymentMethod || 'Not Specified',
                items: Array.isArray(tx.items) ? tx.items : [{ 
                  description: tx.plan?.name || 'Subscription Purchase', 
                  amount: tx.amount || 0,
                  quantity: 1
                }],
                billing: tx.billing || {
                  name: 'User',
                  email: 'user@example.com'
                },
                expiresAt: tx.expiresAt || null,
                createdAt: tx.createdAt || null
              };
            }
            return null;
          })
          .filter(Boolean) as Invoice[];
        
        processedInvoices = transactions;
      }
    }
    
    // Handle array response
    if (Array.isArray(rawData)) {
      processedInvoices = rawData
        .filter(item => item && typeof item === 'object')
        .map(item => {
          // Check if the payment is expired
          const isExpired = this.checkIfPaymentExpired(item);
          const status = isExpired ? 'expired' : (item.status || 'paid');
          
          return {
            id: item._id || item.id || 'invoice-unknown',
            number: item.number || item._id?.substring(0, 8) || 'INV-unknown',
            date: item.date || item.createdAt || item.updatedAt || new Date().toISOString(),
            amount: typeof item.amount === 'number' ? item.amount : 0,
            currency: item.currency || 'INR',
            status: status,
            paymentMethod: item.paymentMethod || 'Not Specified',
            items: Array.isArray(item.items) ? item.items : [{ 
              description: item.plan?.name || 'Subscription Purchase', 
              amount: item.amount || 0,
              quantity: 1
            }],
            billing: item.billing || {
              name: 'User',
              email: 'user@example.com'
            },
            expiresAt: item.expiresAt || null
          };
        });
    }
    
    return processedInvoices;
  }
}

// Export as singleton
const paymentService = new PaymentService();
export default paymentService; 